import { useLingui } from '@lingui/react/macro';

import type { Appointment } from '../../../react-query/useAppointments';
import type { Task } from '../../../react-query/useGetTasks';
import type {
  TProcedure,
  TVisitSummary,
} from '../../../react-query/useVisitSummaries';
import { useUniversalToFDI } from '../../../utils/internationalization/useUniversalToFDI';
import { useMouthAreasGroupedByRecommendedProcedure } from '../patientStrip/patientStripOdontogram/hooks';
import type { TDisplayOrderQuadrantAbbreviations } from '../patientStrip/patientStripOdontogram/types';
import { useGetRecommendationName } from '../patientStrip/patientStripOdontogram/utils';

import { HuddlePrintoutIndicator } from './huddlePrintoutIndicator';
import { QuadrantsGrid } from './quadrantsGrid';
import { RecommendationsCellTasks } from './recommendationsCellTasks';
import { ToothIconAndMouthAreas } from './toothIconAndNumbers';

interface Props {
  appointment: Appointment;
  tasks: Task[];
  visitSummary?: TVisitSummary;
}

export const RecommendationsCell = ({
  appointment,
  tasks,
  visitSummary,
}: Props) => {
  const { displayLocalizedQuadrantShortName, displayToothNumber } =
    useUniversalToFDI();
  const groupedRecommendations =
    useMouthAreasGroupedByRecommendedProcedure(visitSummary);
  const { getRecommendationName } = useGetRecommendationName(true);
  const { t } = useLingui();

  const getMouthArea = (mouthArea: string) => {
    switch (mouthArea) {
      case 'maxillary':
        return t`Upper Arch`;
      case 'mandibular':
        return t`Lower Arch`;
      default:
        return displayToothNumber(mouthArea);
    }
  };

  return (
    <>
      {Object.entries(groupedRecommendations).map(
        ([procedureName, affectedAreas]) => {
          if (!affectedAreas.length) return null;
          return (
            <HuddlePrintoutIndicator
              key={procedureName}
              label={getRecommendationName(procedureName as TProcedure['name'])}
            >
              {procedureName === 'perio' || procedureName === 'locPerio' ? (
                <QuadrantsGrid
                  quadrants={affectedAreas.map(
                    ({ mouthArea: quadAlpha, isAddressed }) =>
                      `${displayLocalizedQuadrantShortName(
                        quadAlpha as TDisplayOrderQuadrantAbbreviations,
                      )}${isAddressed ? ' *' : ''}`,
                  )}
                />
              ) : (
                <ToothIconAndMouthAreas
                  mouthAreas={affectedAreas.map(
                    ({ mouthArea, isAddressed }) =>
                      `${getMouthArea(mouthArea)}${isAddressed ? ' *' : ''}`,
                  )}
                />
              )}
            </HuddlePrintoutIndicator>
          );
        },
      )}
      <RecommendationsCellTasks appointment={appointment} tasks={tasks} />
    </>
  );
};
